import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { PublicIntegrationScopeSchema } from './integrations/integration.models.nango_auth';
import { ConnectionOrSubscriptionMetaSchema } from './integrations/integration.models.operations';
import { AccountRoleSchema } from './models.accounts';
import { ConnectionSettingsSchema } from './models.connection_settings';
import {
  DateTimeTzSchema,
  GenericAccountId,
  GenericConnectionId,
  GenericTimelyAccountId,
  GenericToolId,
  GenericUserID,
} from './models.generic';

extendZodWithOpenApi(z);

/**
 * The `connection.config` copies from Nango and should not be modified directly.
 * These settings may be user-supplied, see `ProviderFormFieldSchema` for more info.
 * @ref /integration-cloud-api-spec/src/models/integrations/integration.models.operations.ts
 */
export type ConnectionConfig = z.infer<typeof ConnectionConfigSchema>;
export const ConnectionConfigFieldNameEnums = ['subdomain', 'instance_url'] as const;
export const ConnectionConfigFieldNameEnumSchema = z
  .enum(ConnectionConfigFieldNameEnums)
  .describe('All supported fields for configuring a Nango connection');

export const ConnectionConfigSchema = z
  .record(ConnectionConfigFieldNameEnumSchema, z.string().nullable().optional())
  .openapi({
    description: 'The (Nango) configuration for a connection',
    example: { subdomain: 'my-subdomain' },
  });

/**
 * Connection Base Schema.
 */
export type ConnectionBase = z.infer<typeof ConnectionBaseSchema>;
export const ConnectionBaseSchema = z.object({
  title: z.string().nullable().describe('Connection display name'),
  scope: PublicIntegrationScopeSchema,
  /**
   * `default` access is "user_id === me" or "account.role = admin+" (BUT IT MAY NOT BE THE DEFAULT FOR NEW CONNECTION).
   * `everyone` only requires user to be in the account.
   *
   * @warning The initial value is now `everyone`; naming convention spans SQL fns, so will leave "'default' as not the default" for now.
   */
  imported_data_access: z
    .enum(['default', 'everyone'])
    .default('everyone')
    .describe('Who can access imported data'),
  tool_id: GenericToolId,
  account_id: GenericAccountId,
  managing_user_id: GenericUserID,
  nango_id: z.number().int().nullable().describe('Nango internal connection ID'),
  nango_connection_id: z.string().nullable().openapi({
    description: 'Nango connection reference ID',
    example: 'account_a1b2c3d4-1234-5678-90ab-cdef12345678',
  }),
  disconnected_at: DateTimeTzSchema.nullable()
    .default(null)
    .describe('When connection was disconnected'),
  disconnection_reason: z
    .enum([
      'authentication_failed',
      'nango_refresh_exhausted',
      'nango_connection_deleted',
      'missing_identity_claim',
    ])
    .nullable()
    .describe('Reason for disconnection'),
  metadata: ConnectionOrSubscriptionMetaSchema,
  config: ConnectionConfigSchema,
  settings: ConnectionSettingsSchema,
});

/**
 * Connection Read Schema.
 */
export type ConnectionRead = z.infer<typeof ConnectionReadSchema>;
export const ConnectionReadSchema = ConnectionBaseSchema.extend({
  id: GenericConnectionId,
  created_at: DateTimeTzSchema,
  updated_at: DateTimeTzSchema,
});

/**
 * Connection Write Schema.
 */
export type ConnectionWrite = z.infer<typeof ConnectionWriteSchema>;
export const ConnectionWriteSchema = ConnectionReadSchema.omit({
  created_at: true,
  updated_at: true,
});

/**
 * Updates to a connection can only touch certain fields.
 */
export type ConnectionUpdate = z.infer<typeof ConnectionUpdateSchema>;
export const ConnectionUpdateSchema = ConnectionWriteSchema.pick({
  id: true,
  // title: true,
  settings: true,
  imported_data_access: true,
});

/**
 * Presentable Connection Schema.
 * Identical to `read` as of now, but will probably diverge so good to be namespaced already.
 */
export type PresentableConnection = z.infer<typeof PresentableConnectionSchema>;
export const PresentableConnectionSchema = ConnectionReadSchema;

/**
 * Returned from `getConnectionContextFromReference`, this gets you
 * the relevant relationships/context based on (nango)connectionId
 */
export type ConnectionContext = z.infer<typeof ConnectionContextSchema>;
export const ConnectionContextSchema = z
  .object({
    account: z.object({
      id: GenericAccountId,
      /** @note in TIC-only dev, we don't have this reliably */
      timely_account_id: GenericTimelyAccountId.nullable(),
    }),
    current_user_role: AccountRoleSchema.nullable()
      .default(null)
      .describe("The authenticated user's role in the account that owns the connection."),
    tool: z.object({
      id: GenericToolId,
      title: z.string(),
    }),
  })
  .merge(
    ConnectionReadSchema.pick({
      id: true,
      nango_connection_id: true,
      managing_user_id: true,
      scope: true,
    })
  );
